import { wsClient } from './websocket';
import { useAuthStore } from '../store/authStore';
import { useVaultStore } from '../store/vaultStore';
import type {
  PublicKey,
  VaultContact,
  VaultContactIdentity,
  SystemEventRecord,
  WSSystemSyncAckData,
  WSSystemSyncResultData
} from '@shared/types';
import { signPayloadWithKey } from '../crypto/messageSigning';
import { systemEventsApi } from '../api/systemEvents';
import { saveSystemEvents, type StoredSystemEvent } from '../systemEvents/storage';
import { t } from '../i18n';
import { showToast } from '../utils/toast';
import { finalizePagedSync } from './pagedSync';
import { resolveStrictMessagingLocalContext } from './actionContextResolver';
import { requestCallHistorySync } from './callHistorySync';
import { buildServerSyncContexts, saveVaultToAllServersWithConflictResolution } from '../utils/vaultSync';

let initialized = false;

async function getSystemSyncContext() {
  const authState = useAuthStore.getState();
  const connectionIdentityId = (wsClient.getConnectionContext()?.identityId || '').toString().trim();
  const currentDeviceId = (authState.currentDeviceId || '').toString().trim();
  const currentIdentityId = currentDeviceId ? (authState.devices[currentDeviceId]?.identityId || '').toString().trim() : '';
  const connectionId = connectionIdentityId || currentIdentityId;
  if (!connectionId) return null;

  const context = await resolveStrictMessagingLocalContext({
    localIdentityId: connectionId,
    timeoutMs: 10_000
  });

  const state = useAuthStore.getState();
  const identity = state.identities[context.localIdentityId];
  if (!identity) return null;

  return {
    deviceId: context.localDeviceId,
    identity
  };
}

function getSystemHttpContext() {
  const state = useAuthStore.getState();
  const connectionIdentityId = (wsClient.getConnectionContext()?.identityId || '').toString().trim();
  const currentDeviceId = (state.currentDeviceId || '').toString().trim();
  const currentIdentityId = currentDeviceId ? (state.devices[currentDeviceId]?.identityId || '').toString().trim() : '';
  const identityId = connectionIdentityId || currentIdentityId;
  if (!identityId) return null;
  const identity = state.identities[identityId];
  if (!identity) return null;
  const device = Object.values(state.devices).find((entry) => entry.identityId === identityId);
  if (!device) return null;
  return { identity, deviceId: device.deviceId };
}

async function applySystemSyncResult(data: WSSystemSyncResultData): Promise<void> {
  await saveSystemEvents(toStored(data.events));
  if (data.events.some((event) => event.type === 'call:missed')) {
    void requestCallHistorySync().catch(() => {});
  }
  handleInviteAcceptedEvents(data.events);
}

async function requestSystemSync(since?: number): Promise<void> {
  const httpContext = getSystemHttpContext();
  if (!httpContext) return;
  const isWsReady = wsClient.isConnectedToServer(httpContext.identity.serverUrl || '')
    && wsClient.getConnectionContext()?.deviceId === httpContext.deviceId;
  if (!isWsReady) {
    const payload = typeof since === 'number' && Number.isFinite(since) ? { since } : {};
    const response = await systemEventsApi.list(payload, httpContext.identity.identityId, httpContext.identity.serverUrl);
    if (!response.result) return;
    await applySystemSyncResult(response.result);
    await finalizePagedSync(response.result, {
      acknowledge: async (syncedThrough) => {
        await systemEventsApi.ack({ syncedThrough }, httpContext.identity.identityId, httpContext.identity.serverUrl);
      },
      continueFrom: requestSystemSync
    });
    return;
  }

  const context = await getSystemSyncContext();
  if (!context) return;
  const payload = typeof since === 'number' && Number.isFinite(since) ? { since } : {};
  const signature = await signPayloadWithKey(payload, context.identity.signingKey);
  wsClient.send({
    type: 'system:sync',
    data: {
      type: 'sys:sync',
      payload,
      deviceId: context.deviceId,
      signature
    },
    timestamp: Date.now()
  });
}

async function sendSystemSyncAck(syncedThrough: number): Promise<void> {
  const context = await getSystemSyncContext();
  if (!context) return;
  const payload = { syncedThrough };
  const signature = await signPayloadWithKey(payload, context.identity.signingKey);
  const data: WSSystemSyncAckData = {
    type: 'sys:sync-ack',
    payload,
    deviceId: context.deviceId,
    signature
  };
  wsClient.send({ type: 'system:sync-ack', data, timestamp: Date.now() });
}

function toStored(events: SystemEventRecord[]): StoredSystemEvent[] {
  return events.map((e) => ({
    eventId: e.eventId,
    serverId: e.serverId,
    recipientIdentityId: e.recipientIdentityId,
    type: e.type,
    payload: e.payload as any,
    serverTimestamp: Number(e.serverTimestamp)
  }));
}

function getInviteNameForEvent(event: SystemEventRecord<'invite:accepted'>): string {
  const authState = useAuthStore.getState();
  const recipientIdentity = authState.identities[event.recipientIdentityId];
  if (!recipientIdentity) {
    return event.payload.acceptedIdentityName || event.payload.acceptedIdentityId;
  }

  const storageKey = `videocall.invite-names:${recipientIdentity.serverId}:${event.recipientIdentityId}`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return event.payload.acceptedIdentityName || event.payload.acceptedIdentityId;
    }
    const map = JSON.parse(raw) as Record<string, string>;
    const localName = map[event.payload.inviteId];
    if (localName && typeof localName === 'string') {
      return localName;
    }
  } catch {
    // ignore
  }
  return event.payload.acceptedIdentityName || event.payload.acceptedIdentityId;
}

function hasInviteAcceptedContact(event: SystemEventRecord<'invite:accepted'>): boolean {
  const authState = useAuthStore.getState();
  const recipientIdentity = authState.identities[event.recipientIdentityId];
  if (!recipientIdentity) return false;

  const contacts = Object.values(useVaultStore.getState().vaultData?.contacts || {});
  return contacts.some((contact) => (
    contact.identities.some((identity) => (
      identity.serverId === recipientIdentity.serverId &&
      identity.identityId === event.payload.acceptedIdentityId
    ))
  ));
}

function addContactFromInviteAccepted(event: SystemEventRecord<'invite:accepted'>): boolean {
  const authState = useAuthStore.getState();
  const recipientIdentity = authState.identities[event.recipientIdentityId];
  if (!recipientIdentity) return false;

  if (hasInviteAcceptedContact(event)) return false;

  const vaultStore = useVaultStore.getState();

  const displayName = getInviteNameForEvent(event);
  const nowIso = new Date().toISOString();
  const contactId = `contact_${crypto.randomUUID()}`;
  const identityPublicKey: PublicKey = event.payload.acceptedIdentityPublicKey;
  const contactIdentity: VaultContactIdentity = {
    serverId: recipientIdentity.serverId,
    identityId: event.payload.acceptedIdentityId,
    serverUrl: recipientIdentity.serverUrl,
    identityPublicKey,
    serverKeySeenAt: nowIso,
    addedAt: nowIso,
    serverIdentityName: event.payload.acceptedIdentityName
  };
  const contact: VaultContact = {
    contactId,
    displayName,
    identities: [contactIdentity],
    createdAt: nowIso
  };
  vaultStore.addContact(contact);

  const serverContexts = buildServerSyncContexts(
    Object.values(authState.identities),
    Object.values(authState.devices),
    new Set([recipientIdentity.serverId])
  );
  if (serverContexts.length > 0) {
    void saveVaultToAllServersWithConflictResolution(serverContexts).catch(() => {
      // The contact remains available locally; background sync is best-effort.
    });
  }
  return true;
}

function handleInviteAcceptedEvents(events: SystemEventRecord[]) {
  const inviteEvents = events.filter((event): event is SystemEventRecord<'invite:accepted'> => (
    event.type === 'invite:accepted'
  ));

  for (const event of inviteEvents) {
    if (hasInviteAcceptedContact(event)) {
      continue;
    }

    const suggestedName = getInviteNameForEvent(event);
    const message = t('systemEvents.inviteAccepted.message', { name: suggestedName });
    showToast({
      message,
      variant: 'success',
      durationMs: 9000,
      actionLabel: t('systemEvents.inviteAccepted.addContactAction'),
      onAction: () => {
        const added = addContactFromInviteAccepted(event);
        showToast({
          message: added
            ? t('systemEvents.inviteAccepted.addedSuccess', { name: suggestedName })
            : t('systemEvents.inviteAccepted.alreadyExists'),
          variant: added ? 'success' : 'info'
        });
      }
    });
  }
}

export function initializeSystemEvents() {
  if (initialized) return;
  initialized = true;

  wsClient.on('registered', () => {
    if (wsClient.isExternalMode()) return;
    void requestSystemSync().catch(() => {});
  });

  wsClient.on('system:sync-result', async (message) => {
    const data = message.data as WSSystemSyncResultData;
    await applySystemSyncResult(data);
    await finalizePagedSync(data, {
      acknowledge: sendSystemSyncAck,
      continueFrom: requestSystemSync
    });
  });
}
